import {
  setEventStatus,
  setConnectionState,
  type EventState,
  type EventErrorState,
  type EventErrorDetails,
  connectionStateStore,
} from '~/lib/stores/eventStatus';

/**
 * Global EventSource connection manager
 * Allows creating and managing SSE connections from anywhere in the app
 */
let globalEventSource: EventSource | null = null;
let connectionParams: { projectId?: string; chatId?: string; userId?: string } | null = null;
let autoCloseTimeout: ReturnType<typeof setTimeout> | null = null;

function clearAutoCloseTimer() {
  if (autoCloseTimeout) {
    clearTimeout(autoCloseTimeout);
    autoCloseTimeout = null;
  }
}

/**
 * Check if a connection is already established
 */
export function isConnected(): boolean {
  const state = connectionStateStore.get();
  return state.isConnected && globalEventSource !== null && globalEventSource.readyState === EventSource.OPEN;
}

/**
 * Check if a connection is currently being established
 */
export function isConnecting(): boolean {
  const state = connectionStateStore.get();
  return state.isConnecting;
}

/**
 * Get current connection state
 */
export function getConnectionState() {
  return connectionStateStore.get();
}

/**
 * Create or reuse an SSE connection for event status updates
 * If a connection already exists with the same parameters, it will be reused.
 * Otherwise, a new connection will be created.
 *
 * @param projectId - Optional project ID filter
 * @param chatId - Optional chat ID filter
 * @param userId - Optional user ID filter
 * @returns Promise that resolves when connection is established or already exists
 */
export function ensureEventStatusConnection(projectId: string, chatId?: string, userId?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    console.log('[EventStatusConnection] ensureEventStatusConnection called with params:', projectId, chatId, userId);

    // Check if we already have a connection with the same parameters
    if (
      isConnected() &&
      connectionParams &&
      connectionParams.projectId === projectId &&
      connectionParams.chatId === chatId &&
      connectionParams.userId === userId
    ) {
      console.log('[EventStatusConnection] Reusing existing connection');
      resolve();
      return;
    }

    // If connecting, wait for it to complete
    if (isConnecting()) {
      console.log('[EventStatusConnection] Connection in progress, waiting...');

      const checkInterval = setInterval(() => {
        if (isConnected()) {
          clearInterval(checkInterval);
          resolve();
        } else if (!isConnecting()) {
          clearInterval(checkInterval);

          // Connection failed, try to create new one
          createConnection(projectId, chatId, userId).then(resolve).catch(reject);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);

        if (!isConnected()) {
          reject(new Error('Connection timeout'));
        }
      }, 10000);
      return;
    }

    // Create new connection
    createConnection(projectId, chatId, userId).then(resolve).catch(reject);
  });
}

/**
 * Create a new SSE connection
 */
function createConnection(projectId?: string, chatId?: string, userId?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Close existing connection if parameters don't match
    if (globalEventSource) {
      console.log('[EventStatusConnection] Closing existing connection to create new one');
      globalEventSource.close();
      globalEventSource = null;
      connectionParams = null;
      clearAutoCloseTimer();
    }

    // Build URL with filters
    const params = new URLSearchParams();

    if (projectId) {
      params.set('projectId', projectId);
    }

    if (chatId) {
      params.set('chatId', chatId);
    }

    if (userId) {
      params.set('userId', userId);
    }

    const url = `/api/event-status-stream${params.toString() ? `?${params.toString()}` : ''}`;

    console.log('[EventStatusConnection] Creating new connection to:', url);
    setConnectionState({ isConnecting: true, error: null });
    connectionParams = { projectId, chatId, userId };

    try {
      const eventSource = new EventSource(url);
      globalEventSource = eventSource;

      // Auto-close the connection after 4 minutes to ensure
      // frontend↔backend and backend↔DB sockets are not kept open indefinitely.
      clearAutoCloseTimer();
      autoCloseTimeout = setTimeout(
        () => {
          console.log('[EventStatusConnection] Auto-closing connection after 4 minutes');

          if (globalEventSource) {
            globalEventSource.close();
            globalEventSource = null;
            connectionParams = null;
          }

          setConnectionState({
            isConnected: false,
            isConnecting: false,
            error: null,
            serverClosed: true,
            closeReason: 'timeout-4m',
            reconnectAttempts: 0,
          });
        },

        // 5 minutes
        5 * 60 * 1000,
      );

      // Handle successful connection open
      eventSource.onopen = () => {
        console.log('[EventStatusConnection] Connection opened');
        setConnectionState({
          isConnected: true,
          isConnecting: false,
          error: null,
          reconnectAttempts: 0,
        });
        resolve();
      };

      // Handle connection established event (custom event from server)
      eventSource.addEventListener('connected', (event) => {
        console.log('[EventStatusConnection] Connected event received:', event.data);
        setConnectionState({
          isConnected: true,
          isConnecting: false,
          error: null,
          reconnectAttempts: 0,
        });
        resolve();
      });

      // Handle server-initiated close (custom "server-closed" event)
      eventSource.addEventListener('server-closed', (event: MessageEvent) => {
        let reason: string | undefined;

        try {
          const data = JSON.parse(event.data || '{}') as { reason?: string; timestamp?: string };
          reason = data.reason;
          console.log('[EventStatusConnection] Server-closed event received:', data);
        } catch {
          console.log('[EventStatusConnection] Server-closed event received (non-JSON payload)');
        }

        if (globalEventSource) {
          globalEventSource.close();
          globalEventSource = null;
          connectionParams = null;
        }

        clearAutoCloseTimer();

        setConnectionState({
          isConnected: false,
          isConnecting: false,
          error: null,
          serverClosed: true,
          closeReason: reason || 'server-closed',
          reconnectAttempts: 0,
        });
      });

      // Handle status updates
      eventSource.addEventListener('status-update', (event) => {
        try {
          const data = JSON.parse(event.data) as {
            eventId: string;
            status: string;
            eventName?: string;
            projectId?: string;
            chatId?: string;
            userId?: string;
            timestamp: string;
            error?: EventErrorDetails;
          };

          console.log('[EventStatusConnection] Status update:', data);

          const derivedEventState: EventErrorState | undefined =
            data.error?.errorType ?? (data.status === 'FAILED' ? 'PROCESSING_ERROR' : undefined);

          const eventState: EventState = {
            status: data.status as any,
            eventState: derivedEventState,
            projectId: data.projectId || '',
            chatId: data.chatId || '',
            userId: data.userId || '',
            timestamp: data.timestamp,
            error: data.error,
          };

          setEventStatus(data.eventId, eventState);
        } catch (error) {
          console.error('[EventStatusConnection] Error parsing status update:', error);
          closeConnection();
        }
      });

      // Handle periodic snapshot updates
      eventSource.addEventListener('snapshot', (event) => {
        try {
          const payload = JSON.parse(event.data) as {
            events?: Array<{
              eventId: string;
              status: string;
              eventName?: string;
              projectId?: string;
              chatId?: string;
              userId?: string;
              timestamp: string;
            }>;
          };

          if (payload?.events && Array.isArray(payload.events)) {
            for (const ev of payload.events) {
              const derivedEventState: EventErrorState | undefined =
                ev.status === 'FAILED' ? 'PROCESSING_ERROR' : undefined;

              setEventStatus(ev.eventId, {
                status: ev.status as any,
                eventState: derivedEventState,
                projectId: ev.projectId || '',
                chatId: ev.chatId || '',
                userId: ev.userId || '',
                timestamp: ev.timestamp,
              });
            }
          }
        } catch (e) {
          console.error('[EventStatusConnection] Error parsing snapshot:', e);
          closeConnection();
        }
      });

      // Handle error messages from server
      eventSource.addEventListener('error', (event: any) => {
        closeConnection();

        if (event.data) {
          try {
            const errorData = JSON.parse(event.data);
            const errorMessage = errorData?.error || errorData?.message || 'Server error';
            console.error('[EventStatusConnection] Server error event:', errorMessage);

            setConnectionState({
              isConnected: false,
              isConnecting: false,
              error: errorMessage,
            });

            if (globalEventSource) {
              globalEventSource.close();
              globalEventSource = null;
              connectionParams = null;
            }

            reject(new Error(errorMessage));
          } catch (e) {
            console.error('[EventStatusConnection] Error parsing server error:', event.data);
          }
        }
      });

      // Handle connection errors (browser-level EventSource errors)
      eventSource.onerror = (error) => {
        console.error('[EventStatusConnection] Connection error, readyState:', eventSource.readyState);

        const errorMessage = 'Failed to connect';
        setConnectionState({
          isConnected: false,
          isConnecting: false,
          error: errorMessage,
        });

        if (globalEventSource) {
          globalEventSource.close();
          globalEventSource = null;
          connectionParams = null;
        }

        clearAutoCloseTimer();
        closeConnection();
        reject(new Error(errorMessage));
      };
    } catch (error) {
      console.error('[EventStatusConnection] Failed to create EventSource:', error);
      setConnectionState({
        isConnected: false,
        isConnecting: false,
        error: 'Failed to create connection',
      });
      closeConnection();
      connectionParams = null;
      reject(error);
    }
  });
}

/**
 * Close the global connection
 */
export function closeConnection() {
  if (globalEventSource) {
    console.log('[EventStatusConnection] Closing connection');
    globalEventSource.close();
    globalEventSource = null;
    connectionParams = null;
    clearAutoCloseTimer();

    setConnectionState({
      isConnected: false,
      isConnecting: false,
      error: null,
      reconnectAttempts: 0,
      serverClosed: false,
      closeReason: null,
    });
  }
}

/**
 * Get the current EventSource instance (for advanced usage)
 */
export function getEventSource(): EventSource | null {
  return globalEventSource;
}
